import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUp, X, BookUser, Loader2, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DirectoryPicker } from "../DirectoryPicker";
import { expLabel } from "../../lib/expirations";
import { encryptSecureFile } from "../../lib/secureFiles/crypto";
import { buildShareLink, formatBytes } from "../../lib/secureFiles/link";
import { fetchSecureFilesConfig, uploadSecureFile, secureFileErrorCode } from "../../lib/secureFiles/api";

const FilePick = ({ file, onPick, onClear, t, lng, disabled }) => {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const drop = (e) => {
    e.preventDefault();
    setOver(false);
    if (!disabled && e.dataTransfer.files?.length) onPick(e.dataTransfer.files[0]);
  };
  if (file) {
    return (
      <div data-testid="sf-selected-file" className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
        <Lock className="w-5 h-5 text-emerald-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <div data-testid="sf-selected-name" className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
          <div data-testid="sf-selected-size" className="text-xs text-slate-500">{formatBytes(file.size, lng)}</div>
        </div>
        <Button data-testid="sf-remove-file" variant="ghost" size="icon" disabled={disabled} onClick={onClear} aria-label={t("sender.remove")}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }
  return (
    <label
      data-testid="sf-dropzone"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={`flex flex-col items-center justify-center text-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 cursor-pointer transition-colors ${over ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}
    >
      <FileUp className="w-8 h-8 text-emerald-600" />
      <span className="text-sm font-semibold text-slate-800">{t("sender.choose")}</span>
      <span className="text-xs text-slate-500">{t("sender.dropHint")}</span>
      <input ref={inputRef} data-testid="sf-file-input" type="file" className="sr-only"
        onChange={(e) => { if (e.target.files?.length) onPick(e.target.files[0]); e.target.value = ""; }} />
    </label>
  );
};

export const SecureFileSender = ({ onCreated }) => {
  const { t, i18n } = useTranslation("files");
  const lng = i18n.resolvedLanguage;
  const [cfg, setCfg] = useState(null);
  const [file, setFile] = useState(null);
  const [expiration, setExpiration] = useState("");
  const [recipient, setRecipient] = useState("");
  const [picker, setPicker] = useState(false);
  const [phase, setPhase] = useState("ready");
  const [pct, setPct] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSecureFilesConfig().then((c) => {
      setCfg(c);
      setExpiration(c.allowed_expirations.includes("24h") ? "24h" : c.allowed_expirations.slice(-1)[0] || "");
    }).catch(() => setCfg({ enabled: false, max_bytes: 0, allowed_expirations: [] }));
  }, []);

  const busy = phase === "encrypting" || phase === "uploading";
  const errText = (code) => t(`errors.${code}`, { size: formatBytes(cfg?.max_bytes || 0, lng), defaultValue: t("errors.UNKNOWN") });

  const pick = (f) => {
    setError(null);
    if (f.size === 0) return setError(errText("EMPTY_FILE"));
    if (f.size > cfg.max_bytes) return setError(errText("FILE_TOO_LARGE"));
    setFile(f);
  };

  const create = async () => {
    setError(null);
    setPct(0);
    try {
      setPhase("encrypting");
      let encrypted = await encryptSecureFile(await file.arrayBuffer(), { name: file.name, mime: file.type });
      setPhase("uploading");
      const data = await uploadSecureFile(encrypted, expiration, setPct);
      const link = buildShareLink(window.location.origin, data.public_path, encrypted.key);
      encrypted = null;
      setFile(null);
      setPhase("ready");
      onCreated({ link, expiresAt: data.expires_at, recipient: recipient.trim() });
    } catch (e) {
      setPhase("error");
      setError(errText(e?.message === "empty_file" ? "EMPTY_FILE" : secureFileErrorCode(e)));
    }
  };

  if (!cfg) return <div data-testid="sf-config-loading" className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t("sender.loadingConfig")}</div>;
  if (!cfg.enabled) return <div data-testid="sf-disabled" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{t("sender.disabled")}</div>;

  return (
    <div data-testid="sf-sender" className="space-y-5">
      <FilePick file={file} onPick={pick} onClear={() => setFile(null)} t={t} lng={lng} disabled={busy} />
      <p data-testid="sf-limits" className="text-xs text-slate-500">{t("sender.oneFile")} {t("sender.maxSize", { size: formatBytes(cfg.max_bytes, lng) })}</p>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t("sender.expiration")}</label>
        <Select value={expiration} onValueChange={setExpiration} disabled={busy}>
          <SelectTrigger data-testid="sf-expiration-select" className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cfg.allowed_expirations.map((k) => <SelectItem key={k} value={k} data-testid={`sf-exp-${k}`}>{expLabel(k)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label htmlFor="sf-recipient" className="text-sm font-medium text-slate-700 mb-1.5 block">{t("sender.recipient")}</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input id="sf-recipient" data-testid="sf-recipient-input" type="email" value={recipient} disabled={busy}
            onChange={(e) => setRecipient(e.target.value)} placeholder={t("sender.recipientPlaceholder")} className="bg-white" />
          <Button data-testid="sf-directory-btn" type="button" variant="outline" disabled={busy} onClick={() => setPicker(true)}>
            <BookUser className="w-4 h-4 mr-2" />{t("sender.pickFromDirectory")}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1.5">{t("sender.recipientHint")}</p>
      </div>

      {busy && (
        <div data-testid="sf-progress" className="space-y-2">
          <div className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
            {phase === "encrypting" ? t("sender.encrypting") : t("sender.uploading", { pct })}</div>
          {phase === "uploading" && <Progress value={pct} className="h-2" />}
        </div>
      )}
      {error && <div data-testid="sf-error" role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <Button data-testid="sf-create-btn" disabled={!file || !expiration || busy} onClick={create}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-11">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}{t("sender.create")}
      </Button>
      <DirectoryPicker open={picker} onOpenChange={setPicker} onSelect={(c) => { setRecipient(c.email || ""); setPicker(false); }} />
    </div>
  );
};

export default SecureFileSender;
